import BarePriceInput from "./BarePriceInput";
import { InputProps } from "components/DesignSystem/TextInput/TextInput";
import { Field, FieldAttributes, FieldProps, getIn } from "formik";
import { ReactNode } from "react";
import { NumberFormatValues, SourceInfo } from "react-number-format";

type PriceInputProps = FieldAttributes<{}> & {
  label?: ReactNode;
  allowNegative?: boolean;
  note?: string | null;
  rightAlign?: boolean;
  rightComponent?: ReactNode;
  customSize?: InputProps["customSize"];
  placeholder?: string | null;
  error?: string;
  hideError?: boolean;
  showErrorOnceTouched?: boolean;
  block?: boolean;
  tooltipText?: string;
  unit?: "USD" | "$" | null;
  max?: number;
  min?: number;
  onValueChange?: (values: NumberFormatValues, sourceInfo: SourceInfo) => void;
  currencyCode?: string;
  showCurrencyFlag?: boolean;
};

export const PriceInput = ({
  unit,
  rightAlign,
  rightComponent,
  onValueChange,
  block,
  note,
  error,
  hideError,
  showErrorOnceTouched,
  tooltipText,
  label,
  customSize,
  currencyCode = "USD",
  showCurrencyFlag = false,
  ...props
}: PriceInputProps) => {
  return (
    <Field {...props}>
      {({ field, form: { errors, setFieldValue, touched } }: FieldProps) => {
        const errorMsg = getIn(errors, field.name);
        const hasError = Boolean(errorMsg && getIn(touched, field.name));
        const showError = !hideError && (hasError || error);

        let variant: "error" | undefined;
        if (showError) variant = "error";

        const helperText = showError
          ? hasError
            ? errorMsg
            : error
          : note ?? undefined;

        return (
          <BarePriceInput
            name={field.name}
            label={typeof label === "string" ? label : undefined}
            required={props.required}
            disabled={props.disabled}
            customSize={customSize}
            placeholder={props.placeholder ?? undefined}
            infoText={tooltipText}
            variant={variant}
            helperText={helperText}
            value={props.value ?? field.value}
            currencyCode={currencyCode}
            showCurrencyFlag={showCurrencyFlag}
            rightComponent={rightComponent}
            onChange={(e) => {
              const numericValue = Number(e.target.value) || 0;
              setFieldValue(field.name, numericValue);
              onValueChange?.(
                {
                  floatValue: numericValue,
                  formattedValue: e.target.value,
                  value: e.target.value,
                },
                { event: e, source: "event" as SourceInfo["source"] }
              );
            }}
          />
        );
      }}
    </Field>
  );
};
